import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { renderAsync } from "docx-preview";
import ReactExcelPreview from "react-excel-preview";
import "react-excel-preview/dist/index.css";
import { emissionFactorManagementAPI } from "../../../EmissionFactorManagement/API/emissionFactorManagementAPI";
import { useTranslation } from "react-i18next";
import { getSafeErrorMessage } from "../../../services/toast";

/**
 * Word 預覽組件
 * 支援傳入本地 File 物件或遠端 link
 */
const WordPreview = ({ file, link }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    // 初始化清空並顯示載入中
    container.innerHTML = "載入文檔中...";

    const renderDocx = (arrayBuffer) => {
      if (!containerRef.current) return;
      container.innerHTML = "";
      renderAsync(arrayBuffer, container, null, {
        className: "docx-viewer",
        inWrapper: true,
        ignoreLastRenderedPageBreak: false,
      }).catch((err) => {
        console.error("Document preview failed", err);
        container.textContent = getSafeErrorMessage();
      });
    };

    if (file) {
      // 本地檔案直接轉為 ArrayBuffer
      file
        .arrayBuffer()
        .then(renderDocx)
        .catch((err) => {
          console.error("Local file read failed", err);
          container.textContent = getSafeErrorMessage();
        });
    } else if (link) {
      // 遠端路徑透過 API 獲取
      emissionFactorManagementAPI
        .previewWordFileAPI(link)
        .then(renderDocx)
        .catch((err) => {
          console.error("Remote document fetch failed", err);
          container.textContent = getSafeErrorMessage();
        });
    }
  }, [file, link]);

  return (
    <div
      style={{
        width: "100%",
        height: "500px",
        padding: "20px",
        overflow: "auto",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          transform: "scale(0.95)",
          transformOrigin: "top left",
          width: "fit-content",
        }}
      >
        <div ref={containerRef} />
      </div>
    </div>
  );
};

/**
 * 核心預覽渲染邏輯
 */
export const FilePreviewContent = ({ file, path, extension }) => {
  const { t } = useTranslation("ActivityMain");
  const ext = (
    extension ||
    (file ? file.name.split(".").pop() : path?.split(".").pop()) ||
    ""
  ).toLowerCase();

  // 如果是本地 File，同步產生 blob URL 以避免首幀路徑為空導致預覽組件失敗
  const [blobUrl] = useState(() => (file ? URL.createObjectURL(file) : null));
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const targetPath = file ? blobUrl : path;

  if (ext === "pdf") {
    return (
      <object
        data={targetPath}
        type="application/pdf"
        width="100%"
        height="500px"
      >
        <p>{t("Unable to preview PDF file.")}</p>
      </object>
    );
  }

  if (ext === "docx") {
    return <WordPreview file={file} link={path} />;
  }

  if (ext === "xlsx" || ext === "xls") {
    return (
      <div style={{ width: "100%", height: "500px" }}>
        <ReactExcelPreview
          options={{ maxColLength: 10 }}
          rowHeight={48}
          showGrid
          showContextmenu={false}
          value={targetPath}
        />
      </div>
    );
  }

  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) {
    return (
      <div
        style={{
          width: "100%",
          height: "500px",
          overflow: "auto",
          backgroundColor: "#f5f5f5",
        }}
      >
        <img
          src={targetPath}
          alt="預覽圖片"
          style={{ display: "block", margin: "0 auto" }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", textAlign: "center" }}>
      {t("This file format does not support preview functionality")}.
    </div>
  );
};

/**
 * 通用的預覽 Modal
 */
const FilePreviewModal = ({ open, onClose, file, path, name, extension }) => {
  const { t } = useTranslation("ActivityMain");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{ zIndex: 10001 }}
    >
      <DialogTitle>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {t("Preview File For")} {name || file?.name}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        <FilePreviewContent file={file} path={path} extension={extension} />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreviewModal;
